<template>
  <div
    id="pdp-contact-form"
    data-test="pdp-contact-form"
    :class="[
      $style.contactFormWrapper,
      $style.wrapper,
      { [$style.containedInModal]: props.isInModal },
    ]"
  >
    <div v-if="shouldShowAgencyCard" :class="$style.agencyCardWrapper">
      <AgencyCard
        :listing="listing"
        :agencyAgent="agencyAgent"
        :isOnTopOfContactForm="true"
        :listerBranding="listerBranding"
        :showPhoneNumbers="showPhoneNumbers"
        :partnerBadge="partnerBadge"
        @phoneNumberReveal="trackContactCallLead($event, ContactActionType.REVEAL_NUMBER_LISTER)"
        @revealedPhoneNumberClick="trackContactCallLead($event, ContactActionType.CALL_LISTER)"
        @phoneNumberClickMobile="trackContactCallLead($event, ContactActionType.CALL_LISTER)"
        @phoneNumberRevealViewing="
          trackContactCallLead($event, ContactActionType.REVEAL_NUMBER_VIEWING)
        "
        @revealedPhoneNumberClickViewing="
          trackContactCallLead($event, ContactActionType.CALL_VIEWING)
        "
        @phoneNumberClickMobileViewing="
          trackContactCallLead($event, ContactActionType.CALL_VIEWING)
        "
      />
    </div>
    <component
      :is="contactRequestEmailVerificationPanel"
      v-if="shouldShowContactRequestEmailVerificationPanel"
      :email="contactFormStore.contactRequestEmail"
      :contactRequestId="contactFormStore.contactInquiryId"
      @back="handleContactRequestEmailVerificationBack"
    />
    <h3 v-else-if="isAddressRequested" :class="$style.textCenter">
      {{ t('contactForm.REQUEST_ADDRESS') }}
    </h3>
    <h3 v-else-if="!shouldShowAgencyCard">
      {{ t('contactForm.CONTACT_ADVERTISER') }}
    </h3>
    <h3 v-else-if="shouldShowAgencyCard" :class="$style.mobileContactAdvertiserHeader">
      {{ t('contactForm.CONTACT_ADVERTISER') }}
    </h3>

    <TenantPlusHighlightMessage
      v-if="!shouldShowContactRequestEmailVerificationPanel && shouldShowTenantPlusHighlightBanner"
      variant="banner"
    />

    <form
      v-if="!shouldShowContactRequestEmailVerificationPanel"
      :class="$style.wrapper"
      novalidate
      @submit.prevent="handleFormSubmit"
    >
      <div :class="[$style.gridWrapper, { [$style.formCollapsed]: isFormCollapsed }]">
        <div :class="$style.wrapper">
          <div :class="$style.row">
            <HgInput
              v-model.trim="formState.firstName"
              :label="t('contactForm.FIRST_NAME')"
              name="firstName"
              :isRequired="!!v$.firstName.required"
              :isInvalid="v$.firstName.$invalid"
              :isDirty="v$.firstName.$dirty"
              :maxlength="150"
              @update:model-value="v$.firstName.$touch()"
            >
              <template #errorMessages>
                <span v-if="v$.firstName.required.$invalid">
                  {{ t('validation.firstNameRequired') }}
                </span>
              </template>
            </HgInput>
            <HgInput
              v-model.trim="formState.lastName"
              :label="t('contactForm.LAST_NAME')"
              name="lastName"
              :isRequired="!!v$.lastName.required"
              :isInvalid="v$.lastName.$invalid"
              :isDirty="v$.lastName.$dirty"
              :maxlength="150"
              @update:model-value="v$.lastName.$touch()"
            >
              <template #errorMessages>
                <span v-if="v$.lastName.required.$invalid">
                  {{ t('validation.lastNameRequired') }}
                </span>
              </template>
            </HgInput>
          </div>

          <HgInput
            v-model.trim="formState.email"
            :label="t('contactForm.EMAIL')"
            name="email"
            type="email"
            :disabled="isUserLoggedIn"
            :isRequired="!!v$.email.required"
            :isInvalid="v$.email.$invalid"
            :isDirty="v$.email.$dirty"
            @update:model-value="v$.email.$touch()"
          >
            <template #errorMessages>
              <span v-if="v$.email.required.$invalid">
                {{ t('validation.phoneOrEMailRequired') }}
              </span>
              <span v-if="v$.email.email.$invalid">{{ t('validation.emailInvalid') }}</span>
            </template>
          </HgInput>

          <HgInput
            v-model.trim="formState.phone"
            :label="t('contactForm.PHONE')"
            name="phone"
            type="tel"
            :isRequired="!!v$.phone.required"
            :isInvalid="v$.phone.$invalid"
            :isDirty="v$.phone.$dirty"
            :maxlength="150"
            @update:model-value="v$.phone.$touch()"
          >
            <template #errorMessages>
              <span v-if="v$.phone.required.$invalid">
                {{ t('validation.phoneOrEMailRequired') }}
              </span>
            </template>
          </HgInput>

          <div v-if="isFormWithAddress" :class="$style.wrapper">
            <HgInput
              v-model.trim="formState.street"
              :label="t('contactForm.STREET')"
              name="street"
              :isRequired="!!v$.street.required"
              :isInvalid="v$.street.$invalid"
              :isDirty="v$.street.$dirty"
              :maxlength="150"
              @update:model-value="v$.street.$touch()"
            >
              <template #errorMessages>
                <span v-if="v$.street.required.$invalid">
                  {{ t('validation.streetRequired') }}
                </span>
              </template>
            </HgInput>
            <div :class="$style.row">
              <HgInput
                v-model="formState.zip"
                name="zip"
                type="number"
                :label="t('contactForm.ZIP')"
                :isRequired="!!v$.zip.required"
                :isInvalid="v$.zip.$invalid"
                :isDirty="v$.zip.$dirty"
                :maxlength="150"
                @update:model-value="v$.zip.$touch()"
              >
                <template #errorMessages>
                  <span v-if="v$.zip.required.$invalid">{{ t('validation.zipRequired') }}</span>
                </template>
              </HgInput>
              <HgInput
                v-model.trim="formState.city"
                name="city"
                :label="t('contactForm.CITY')"
                :class="$style.addressCityInput"
                :isRequired="!!v$.city.required"
                :isInvalid="v$.city.$invalid"
                :isDirty="v$.city.$dirty"
                :maxlength="150"
                @update:model-value="v$.city.$touch()"
              >
                <template #errorMessages>
                  <span v-if="v$.city.required.$invalid">{{ t('validation.cityRequired') }}</span>
                </template>
              </HgInput>
            </div>
          </div>

          <LeadQualificationFieldsForBuy
            v-if="showLeadQualificationFieldsForBuy"
            :initialEmploymentStatus="employmentStatus"
            :initialMonthlyIncome="monthlyIncome"
            :initialEquityCapital="equityCapital"
            @onEmploymentStatusChanged="handleEmploymentStatusSelectedValue"
            @onMonthlyIncomeChanged="handleMonthlyIncomeSelectedValue"
            @onEquityCapitalChanged="handleEquityCapitalInputValue"
          />

          <HgTextArea
            v-model="formState.message"
            :class="$style.textArea"
            :label="t('contactForm.MESSAGE')"
            :isRequired="!!v$.message.required"
            :isInvalid="v$.message.$invalid"
            :isDirty="v$.message.$dirty"
            :updateModelOnInput="false"
            name="message"
            @update:model-value="v$.message.$touch()"
          >
            <template #errorMessages>
              <span>{{ t('validation.messageRequired') }}</span>
            </template>
          </HgTextArea>
          <LeadQualificationFieldsForRent
            v-if="showLeadQualificationFieldsForRent"
            :listing="listing"
            :initialMonthlyIncome="monthlyIncome"
            :hadProfileAlready="showProfileCheckbox"
            @onMonthlyIncomeChanged="handleMonthlyIncomeSelectedValue"
          />
        </div>
      </div>

      <ContactedOnMessage
        v-if="contactedDate && showContactedOnMessage"
        :contactedDate="contactedDate"
      />

      <template v-if="isSubmittedAsAddressRequest === isAddressRequested">
        <HgNotificationMessage
          v-if="isFormSubmittedWithSuccess && !shouldShowContactRequestEmailVerificationPanel"
          data-test="pdp-contact-form-success-notification"
          :title="t('contactForm.submitFeedback.success.title')"
          theme="success"
        >
          {{ t('contactForm.submitFeedback.success.message') }}
          <TenantPlusHighlightMessage
            v-if="isTenantPlusSubscriberAndExclusiveListing"
            variant="success"
          />
        </HgNotificationMessage>

        <HgNotificationMessage
          v-if="isFormSubmittedWithOfflineError"
          data-test="pdp-contact-form-offline-notification"
          :title="t('contactForm.submitFeedback.error.title')"
          theme="danger"
        >
          {{ t('contactForm.submitFeedback.error.listingOffline') }}
        </HgNotificationMessage>

        <HgNotificationMessage
          v-else-if="isFormSubmittedWithError"
          data-test="pdp-contact-form-error-notification"
          :title="t('contactForm.submitFeedback.error.title')"
          theme="danger"
        >
          {{ t('contactForm.submitFeedback.error.message') }}
        </HgNotificationMessage>
      </template>

      <HaTSLink
        v-if="showHaTS"
        :title="t('HaTS.title')"
        :text="t('HaTS.text')"
        :url="t('HaTS.url')"
        :linkText="t('HaTS.linkText')"
      />

      <div v-if="showProfileCheckbox">
        <HgCheckbox
          v-model="isProfileAttached"
          :label="t('contactForm.profileCheckbox.label')"
          name="isProfileAttached"
        />
        <div :class="$style.previewProfileText">
          <HgLink
            :class="$style.previewProfileCTA"
            tag="button"
            type="button"
            @click="onClickProfilePreviewCTA"
          >
            {{ t('contactForm.profileCheckbox.previewText') }}
          </HgLink>
        </div>
      </div>

      <div v-if="shouldShowSellerLeadDropdown && !isFormCollapsed">
        <HgSelect
          ref="sellerLeadDropdown"
          v-model="owner"
          name="sellerLeadDropdown"
          :options="sellerLeadDropdownOptions"
          :label="t('contactForm.sellerLeadDropdown.label')"
        />
      </div>

      <HgButton type="submit" :class="$style.submitButton" :theme="submitButtonTheme">
        <template v-if="isSubmitInProgress" #icon><LoadingSpinner /></template>
        {{ !isSubmitInProgress ? submitButtonText : undefined }}
      </HgButton>

      <section v-if="!isFormCollapsed" :class="$style.generalConditions">
        <i18n-t keypath="TnCText.textContactForm" tag="p" scope="global">
          <template #gtc>
            <a :href="t('TnCText.gtc.link')" target="_blank" rel="noopener">{{
              t('TnCText.gtc.name')
            }}</a>
          </template>
          <template #ds>
            <a :href="t('TnCText.ds.link')" target="_blank" rel="noopener">{{
              t('TnCText.ds.name')
            }}</a>
          </template>
        </i18n-t>
      </section>

      <section
        v-if="shouldShowSellerLeadDropdown && !isFormCollapsed"
        id="sellerLeads"
        :class="$style.sellerLeads"
      >
        <i18n-t
          keypath="contactForm.SELLER_LEADS_PRIVACY_TEXT.PRE_LINK_TEXT"
          tag="p"
          scope="global"
        >
          <template #LINK_TEXT>
            <HgLink :class="$style.sellerLeadsPrivacyLink" @click="openSellerLeadsPrivacyPopup">
              {{ t('contactForm.SELLER_LEADS_PRIVACY_TEXT.LINK_TEXT') }}
            </HgLink>
          </template>
        </i18n-t>
      </section>
    </form>
    <ContactRequestEmailVerificationCodeModal
      v-if="shouldShowEmailVerificationCodeModal"
      :isVisible="shouldShowEmailVerificationCodeModal"
      :email="contactFormStore.contactRequestEmail"
      :contactRequestId="contactFormStore.contactInquiryId!"
      @close="handleContactRequestEmailVerificationCodeModalClose"
    />
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable max-lines */
import {
  ButtonTheme,
  HgButton,
  HgCheckbox,
  HgInput,
  HgLink,
  HgNotificationMessage,
  HgSelect,
  HgSelectOptions,
  HgSupportedLanguage,
  HgTextArea,
} from '@smg-real-estate/domus';
import { Listing, OfferType, Size } from '@smg-real-estate/hg-rets-schema';
import {
  AgencyAgent,
  ListerBranding,
  SearchModel,
  StorageHelper,
} from '@smg-real-estate/hg-search-libs';
import { HaTSLink } from '@smg-real-estate/seeker-app-shared-components';
import { useVuelidate } from '@vuelidate/core';
import { email, required, requiredIf } from '@vuelidate/validators';
import { computed, defineAsyncComponent, onMounted, onUnmounted, reactive, ref, watch } from 'vue';

import ContactedOnMessage from '@/components/ContactedOnMessage/ContactedOnMessage.vue';
import TenantPlusHighlightMessage from '@/components/TenantPlus/TenantPlusHighlightMessage.vue';
import {
  isUserLoggedIn as checkUserLoggedIn,
  getAuthToken,
  getUserLoginInfo,
} from '@/helpers/Authentication/authClient';
import {
  getContactedOnDateForListing,
  getContactFormRecaptchaSiteKey,
  setContactedOnDateForListing,
} from '@/helpers/ContactForm/ContactFormHelper';
import FeatureFlags from '@/helpers/FeatureToggle/FeatureFlags';
import { isFeatureEnabled } from '@/helpers/FeatureToggle/FeatureToggleHelper';
import { InteractionContainerDetail } from '@/helpers/GTM/GTMParameters';
import { resolveUrl } from '@/helpers/HgLink/HgLinkHelper';
import { ppaListerPrefixes } from '@/helpers/ListingStats/types';
import type { PartnerBadge } from '@/helpers/partnerBadge/getPartnerBadge';
import {
  getRecaptchaToken,
  hideRecaptchaBadge,
  showRecaptchaBadge,
} from '@/helpers/Recaptcha/recaptchaHelper';
import { getLastSearch } from '@/helpers/SessionStorage/searchResultStorage';
import { StorageNamespace } from '@/helpers/storage-namespaces';
import { getProfileId } from '@/helpers/UserProfile/UserProfileHelper';
import { HgLocalStorageKey } from '@/models';
import { generateSearchHeading } from '@/resultlist/meta';
import { createPayloadSearchCriteria } from '@/resultlist/sns';
import { SplitFeatureFlags } from '@/splitio/splitFeatureFlags';
import {
  ContactFormData,
  ContactFormSubmitStatus,
  OwnerChecked,
} from '@/store/types/contact-form-state';

import { useContactFormStore } from '@/store/modules/contact-form';
import { useGeoStore } from '@/store/modules/geo';
import { useSplitStore } from '@/store/modules/split';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  ContactActionType,
  trackContactCallLead,
  trackContactFormExpanded,
  trackContactFormProfilePreviewClick,
} from './contact-form-tracking-events';
import ContactRequestEmailVerificationCodeModal from './ContactRequestEmailVerificationCodeModal.vue';
import ContactRequestEmailVerificationCodePanel from './ContactRequestEmailVerificationCodePanel.vue';
import ContactRequestEmailVerificationEnforcePanel from './ContactRequestEmailVerificationEnforcePanel.vue';
import LeadQualificationFieldsForBuy from './LeadQualificationFieldsForBuy.vue';
import LeadQualificationFieldsForRent from './LeadQualificationFieldsForRent.vue';

const route = useRoute();
const { t, locale } = useI18n();
const contactFormStore = useContactFormStore();
const splitStore = useSplitStore();

defineOptions({
  name: 'ContactForm',
});

const AgencyCard = defineAsyncComponent(() => import('@/components/AgencyCard/AgencyCard.vue'));
const LoadingSpinner = defineAsyncComponent(() => import('@/assets/loading_spinner.svg'));

interface Props {
  listing: Listing;
  agencyAgent?: AgencyAgent;
  listerBranding?: ListerBranding | null;
  isAddressRequested?: boolean;
  shouldShowAgencyCard?: boolean;
  showPhoneNumbers?: boolean;
  partnerBadge?: PartnerBadge | null;
  isTenantPlusSubscriber?: boolean;
  isInModal?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  agencyAgent: undefined,
  listerBranding: null,
  isAddressRequested: false,
  shouldShowAgencyCard: false,
  showPhoneNumbers: true,
  partnerBadge: null,
  isTenantPlusSubscriber: false,
  isInModal: false,
});

const emit = defineEmits<{
  contactRequestDispatched: [];
  openPrivacyDeclarationModalVisible: [];
  formSubmitSuccess: [
    isFormWithAddress: boolean,
    contactInquiryId: string | undefined,
    isOwnerChecked: boolean,
    hasExtraFinanceInformation: boolean,
    isEmailVerificationRequired: boolean,
    isProfileAttached?: boolean,
  ];
  postSubmitModalOpen: [];
  formSubmitBlocked: [];
  formSubmitError: [isFormWithAddress: boolean, error: Error | null];
  contactFormMounted: [isFormWithAddress: boolean];
}>();

const geoStore = useGeoStore();

const isFormWithAddressRequired = ref();
const formState = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  zip: '',
  city: '',
  message: '',
});

const isUserLoggedIn = ref(false);

const rules = computed(() => ({
  firstName: { required },
  lastName: { required },
  email: {
    required: requiredIf(() => !isUserLoggedIn.value),
    email,
  },
  phone: {},
  street: {
    required: requiredIf(isFormWithAddressRequired),
  },
  zip: {
    required: requiredIf(isFormWithAddressRequired),
  },
  city: {
    required: requiredIf(isFormWithAddressRequired),
  },
  message: { required },
}));
const v$ = useVuelidate(rules, formState);

const storageHelper = new StorageHelper(StorageNamespace.detailsPage);
const FormSizesWithAddress = [Size.WithAddress, Size.Maxi, Size.Flowfact];

let contactFormContentSnapshot: any = {};
if (!props.isAddressRequested) {
  const storageSnapshot = storageHelper.getLocalStorageItem(
    HgLocalStorageKey.CONTACT_FORM_CONTENT_SNAPSHOT,
  );
  if (storageSnapshot) {
    storageSnapshot.owner = OwnerChecked.default;
  }
  contactFormContentSnapshot = storageSnapshot;
}
const employmentStatus = ref(
  (contactFormContentSnapshot as { employmentStatus: string | undefined })?.employmentStatus,
);
const monthlyIncome = ref(
  (contactFormContentSnapshot as { monthlyIncome: string | undefined })?.monthlyIncome,
);
const equityCapital = ref(
  (contactFormContentSnapshot as { equityCapital: number | undefined })?.equityCapital,
);

// calculate initial message value
let message = (contactFormContentSnapshot as { message: string })?.message;

if (message === undefined) {
  if (props.isAddressRequested) {
    message = t('contactForm.MESSAGE_REQUEST_ADDRESS').toString();
  } else {
    message = t('contactForm.MESSAGE_PLACEHOLDER').toString();
  }
}

// Don't destructure as it removed the reactivity
formState.firstName = contactFormContentSnapshot?.firstName;
formState.lastName = contactFormContentSnapshot?.lastName;
formState.email = contactFormContentSnapshot?.email;
formState.phone = contactFormContentSnapshot?.phone;
formState.street = contactFormContentSnapshot?.street;
formState.zip = contactFormContentSnapshot?.zip;
formState.city = contactFormContentSnapshot?.city;
formState.message = message;

const isFormCollapsed = ref(false);
const isFormSubmitInProgress = ref(false);
const isFormSubmittedWithSuccess = ref(false);
const isFormSubmittedWithError = ref(false);
const isFormSubmittedWithOfflineError = ref(false);
const owner = ref(OwnerChecked.default);
const contactedDate = ref<string | undefined>(undefined);
const isProfileAttached = ref(true);
const profileId = ref<string | undefined>(undefined);
const hasExtraFinanceInformation = ref(false);
const recaptchaToken = ref<string>('');
const isRecaptchaError = ref(false);
const isRecaptchaSubmitInProgress = ref(false);

const isFormWithAddress = computed((): boolean => {
  const formSize = props.listing?.contactForm?.size as Size;
  return FormSizesWithAddress.includes(formSize);
});
const formSubmitStatus = computed((): ContactFormSubmitStatus => {
  return contactFormStore.contactFormSubmitStatus;
});
const isSubmitInProgress = computed((): boolean => {
  return (
    isRecaptchaSubmitInProgress.value ||
    isFormSubmitInProgress.value ||
    formSubmitStatus.value === ContactFormSubmitStatus.IN_PROGRESS
  );
});
const listingId = computed((): string | undefined => {
  return props.listing.id || undefined;
});
const submitButtonText = computed((): string => {
  return (
    isFormCollapsed.value
      ? t('contactForm.submitFeedback.request.cta')
      : t('contactForm.SUBMIT_REQUEST')
  ) as string;
});
const submitButtonTheme = computed((): ButtonTheme => {
  return isFormCollapsed.value ? ButtonTheme.SecondaryBordered : ButtonTheme.Primary;
});

const isContactRequestEmailVerificationFeatureEnabled = (): boolean => {
  return isFeatureEnabled(FeatureFlags.ENABLE_CONTACT_REQUEST_EMAIL_VERIFICATION, route);
};

// Read treatments directly from the store so Vue tracks updates after SSR hydration
// and the post-consent Split refresh. splitStore.getTreatment() is not reliably reactive
// when called inside computed properties.
const emailVerificationPanelTreatment = computed(
  () => splitStore.treatments[SplitFeatureFlags.KK_2784_EMAIL_VERIFICATION_FLOWS_PDP]?.treatment,
);

const isEmailVerificationExperimentActive = computed((): boolean => {
  return emailVerificationPanelTreatment.value === 'link';
});

const isEmailVerificationCodeExperimentActive = computed((): boolean => {
  return (
    emailVerificationPanelTreatment.value === 'code' ||
    emailVerificationPanelTreatment.value === 'codeModal'
  );
});

const isEmailVerificationCodeModalExperimentActive = computed((): boolean => {
  return emailVerificationPanelTreatment.value === 'codeModal';
});

const contactRequestEmailVerificationPanel = computed(() => {
  if (emailVerificationPanelTreatment.value === 'code') {
    return ContactRequestEmailVerificationCodePanel;
  }

  return ContactRequestEmailVerificationEnforcePanel; // default (control group)
});

const isContactRequestEmailVerificationActive = computed((): boolean => {
  return (
    formSubmitStatus.value === ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED &&
    contactFormStore.isContactRequestEmailVerificationRequired &&
    !contactFormStore.isSubmittedByAuthenticatedUser
  );
});

const shouldShowContactRequestEmailVerificationPanel = computed((): boolean => {
  return (
    isContactRequestEmailVerificationActive.value &&
    !isEmailVerificationCodeModalExperimentActive.value
  );
});

const shouldShowEmailVerificationCodeModal = computed((): boolean => {
  return (
    isContactRequestEmailVerificationActive.value &&
    isEmailVerificationCodeModalExperimentActive.value
  );
});
const showHaTS = computed((): boolean => {
  return (
    !isTenantPlusSubscriberAndExclusiveListing.value &&
    (isFeatureEnabled(FeatureFlags.ENABLE_HATS, route) ||
      (isFormSubmittedWithSuccess.value && !shouldShowContactRequestEmailVerificationPanel.value))
  );
});
const showContactedOnMessage = computed((): boolean => {
  return isFormCollapsed.value && formSubmitStatus.value === ContactFormSubmitStatus.NOT_SUBMITTED;
});
const resultSearchModel = computed((): SearchModel | null => {
  return getLastSearch();
});
const isSubmittedAsAddressRequest = computed((): boolean => {
  return contactFormStore.isSubmittedAsAddressRequest;
});
const shouldShowSellerLeadDropdown = computed((): boolean => {
  return !!props.listing && props.listing.offerType === OfferType.Buy;
});
const isPPARentListing = computed((): boolean => {
  return (
    props.listing.offerType === OfferType.Rent &&
    ppaListerPrefixes.some((prefix) => props.listing?.lister?.id?.startsWith(prefix))
  );
});
const showLeadQualificationFieldsForRent = computed((): boolean => {
  return props.listing.offerType === OfferType.Rent;
});
const showLeadQualificationFieldsForBuy = computed((): boolean => {
  return props.listing.offerType === OfferType.Buy;
});
const userProfileUrl = computed((): string => {
  const callbackUrl = resolveUrl(route.fullPath);
  const userProfileUrl = new URL(
    `${import.meta.env.VUE_APP_BASE_URL}/${locale.value}/user-profile/profile`,
  );
  userProfileUrl.searchParams.append('callbackUrl', callbackUrl.toString());
  return userProfileUrl.toString();
});
// Logged-in users must submit their Auth0 account email; leave formState.email unchanged when logged out.
const fillInAuthenticatedUserEmail = async (): Promise<void> => {
  const userInfo = (await checkUserLoggedIn()) ? await getUserLoginInfo() : null;

  if (!userInfo) {
    isUserLoggedIn.value = false;
    return;
  }

  isUserLoggedIn.value = true;
  formState.email = userInfo.email ?? '';
};
const showProfileCheckbox = computed((): boolean => {
  return !!profileId.value && isPPARentListing.value;
});
const sellerLeadDropdownOptions = computed((): HgSelectOptions[] => {
  return [
    {
      text: t('contactForm.sellerLeadDropdown.options.default') as string,
      value: OwnerChecked.default,
      disabled: true,
    },
    {
      text: t('contactForm.sellerLeadDropdown.options.yes') as string,
      value: OwnerChecked.checked,
    },
    {
      text: t('contactForm.sellerLeadDropdown.options.no') as string,
      value: OwnerChecked.notChecked,
    },
  ];
});

const isTenantPlusSubscriberAndExclusiveListing = computed((): boolean => {
  return props.isTenantPlusSubscriber && isExclusiveListing.value;
});
const isExclusiveListing = computed((): boolean => {
  return Boolean(props.listing?.valueAddedServices?.isTenantPlusListing);
});
const shouldShowTenantPlusHighlightBanner = computed((): boolean => {
  return !props.isAddressRequested && props.isTenantPlusSubscriber && isExclusiveListing.value;
});

const scrollContactFormIntoView = () => {
  document.getElementById('pdp-contact-form')?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  });
};

watch(formSubmitStatus, (newContactFormSubmitStatus, previousContactFormSubmitStatus) => {
  isFormSubmitInProgress.value = newContactFormSubmitStatus === ContactFormSubmitStatus.IN_PROGRESS;
  isFormSubmittedWithSuccess.value = newContactFormSubmitStatus === ContactFormSubmitStatus.SUCCESS;
  isFormSubmittedWithError.value = newContactFormSubmitStatus === ContactFormSubmitStatus.ERROR;
  isFormSubmittedWithOfflineError.value =
    newContactFormSubmitStatus === ContactFormSubmitStatus.LISTING_OFFLINE;

  const completedInlineEmailVerification =
    newContactFormSubmitStatus === ContactFormSubmitStatus.SUCCESS &&
    previousContactFormSubmitStatus === ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED;

  switch (newContactFormSubmitStatus) {
    case ContactFormSubmitStatus.NOT_SUBMITTED:
    case ContactFormSubmitStatus.IN_PROGRESS:
      break;
    case ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED:
      if (!isEmailVerificationCodeModalExperimentActive.value) {
        isFormCollapsed.value = true;
      }
      scrollContactFormIntoView();
      recaptchaToken.value = '';
      isRecaptchaError.value = false;

      // we should still emit formSubmitSuccess for the tracking event "submit_formlead"
      emit(
        'formSubmitSuccess',
        isFormWithAddress.value,
        contactFormStore.contactInquiryId,
        owner.value === OwnerChecked.checked,
        hasExtraFinanceInformation.value,
        true, // isEmailVerificationRequired
        showProfileCheckbox.value ? isProfileAttached.value : undefined,
      );
      break;
    case ContactFormSubmitStatus.SUCCESS:
      isFormCollapsed.value = true;
      scrollContactFormIntoView();
      recaptchaToken.value = '';
      isRecaptchaError.value = false;

      listingId.value && saveContactedDate(listingId.value);

      if (!completedInlineEmailVerification) {
        // we already emitted this event previously in "ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED" case. We can skip it here to avoid duplicates.
        emit(
          'formSubmitSuccess',
          isFormWithAddress.value,
          contactFormStore.contactInquiryId,
          owner.value === OwnerChecked.checked,
          hasExtraFinanceInformation.value,
          false, // isEmailVerificationRequired
          showProfileCheckbox.value ? isProfileAttached.value : undefined,
        );
      } else {
        emit('postSubmitModalOpen');
      }
      break;
    case ContactFormSubmitStatus.BLOCKED:
      emit('formSubmitBlocked');
      contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.NOT_SUBMITTED);
      break;
    case ContactFormSubmitStatus.ERROR:
    case ContactFormSubmitStatus.LISTING_OFFLINE:
    default:
      emit('formSubmitError', isFormWithAddress.value, contactFormStore.contactFormSubmitError);
      break;
  }
});

onMounted(async () => {
  if (props.listing.id) {
    contactedDate.value = getContactedOnDateForListing(props.listing.id);
    isFormCollapsed.value = Boolean(contactedDate.value);
  }
  emit('contactFormMounted', isFormWithAddress.value);
  isFormWithAddressRequired.value = isFormWithAddress.value;

  await fillInAuthenticatedUserEmail();

  if (isPPARentListing.value) {
    profileId.value = await getProfileId();
  }
});

onUnmounted(() => {
  hideRecaptchaBadge();
});

const handleFormSubmit = () => {
  if (props.listing.lister?.allowToContact === false) {
    contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.BLOCKED);
    return;
  }

  if (isSubmitInProgress.value) {
    return;
  }

  if (isFormCollapsed.value) {
    isFormCollapsed.value = false;
    scrollContactFormIntoView();

    contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.NOT_SUBMITTED);

    trackContactFormExpanded(
      isFormSubmittedWithSuccess.value
        ? InteractionContainerDetail.CONTACT_FORM_AFTER_SUBMIT
        : InteractionContainerDetail.REVISIT_CONTACTED_PDP,
    );
    return;
  }

  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  sendContactRequest();
};

const handleContactRequestEmailVerificationBack = () => {
  isFormCollapsed.value = false;
  contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.NOT_SUBMITTED);
};

const handleContactRequestEmailVerificationCodeModalClose = () => {
  if (formSubmitStatus.value === ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED) {
    contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.NOT_SUBMITTED);
  }
};

const refreshRecaptchaToken = async (): Promise<boolean> => {
  const siteKey = getContactFormRecaptchaSiteKey();
  if (!siteKey) return true;
  try {
    showRecaptchaBadge();
    recaptchaToken.value = await getRecaptchaToken(siteKey, locale.value, 'contact_form_submit');
    return true;
  } catch {
    isRecaptchaError.value = true;
    return false;
  }
};

const persistContactFormContentSnapshot = (contactInformation: ContactFormData): void => {
  if (props.isAddressRequested) return;

  const contactFormContentSnapshot = { ...contactInformation };
  delete contactFormContentSnapshot.recaptchaToken;

  storageHelper.setLocalStorageItem(HgLocalStorageKey.CONTACT_FORM_CONTENT_SNAPSHOT, {
    ...contactFormContentSnapshot,
    requestSent: new Date(),
  });
};

const createBaseContactInformation = (): ContactFormData => ({
  firstName: formState.firstName,
  lastName: formState.lastName,
  phone: formState.phone,
  email: formState.email,
  message: formState.message,
  owner: owner.value === OwnerChecked.checked ? OwnerChecked.checked : OwnerChecked.notChecked,
  recaptchaToken: recaptchaToken.value || undefined,
});

const addSearchAlertInformation = (contactInformation: ContactFormData): void => {
  if (!resultSearchModel.value?.locations?.length || !formState.email) return;

  contactInformation.searchAlertCriteria = createPayloadSearchCriteria(resultSearchModel.value);
  contactInformation.searchAlertName = generateSearchHeading(
    resultSearchModel.value,
    t,
    geoStore,
    locale.value as HgSupportedLanguage,
    undefined,
  );
};

const addAddressInformation = (contactInformation: ContactFormData): void => {
  if (!isFormWithAddress.value) return;

  contactInformation.street = formState.street;
  contactInformation.zip = formState.zip;
  contactInformation.city = formState.city;
};

const addLeadQualificationInformation = (contactInformation: ContactFormData): void => {
  if (showLeadQualificationFieldsForRent.value) {
    contactInformation.monthlyIncome = monthlyIncome.value;
    return;
  }

  if (showLeadQualificationFieldsForBuy.value) {
    contactInformation.employmentStatus = employmentStatus.value;
    contactInformation.monthlyIncome = monthlyIncome.value;
    contactInformation.equityCapital = equityCapital.value;
  }
};

const createContactInformation = (): ContactFormData => {
  const contactInformation = createBaseContactInformation();

  addSearchAlertInformation(contactInformation);
  addAddressInformation(contactInformation);
  addLeadQualificationInformation(contactInformation);

  return contactInformation;
};

const sendContactRequest = async () => {
  isRecaptchaSubmitInProgress.value = true;

  try {
    if (!(await refreshRecaptchaToken())) return;

    const contactInformation = createContactInformation();
    const accessToken = await getAuthToken();
    const shouldOptInToEmailVerification =
      !accessToken && isContactRequestEmailVerificationFeatureEnabled();
    const isInEmailVerificationExperiment =
      !accessToken && isEmailVerificationExperimentActive.value;
    const isInEmailVerificationCodeExperiment =
      !accessToken && isEmailVerificationCodeExperimentActive.value;

    contactFormStore.contactFormSubmitStatusChange(ContactFormSubmitStatus.IN_PROGRESS);
    contactFormStore.sendContactRequest({
      id: listingId.value,
      contactInformation: {
        ...contactInformation,
        language: locale.value,
      },
      submittedAsAddressRequest: props.isAddressRequested,
      isTenantPlusSubscriber:
        props.isTenantPlusSubscriber ||
        !!(
          props.listing.lister?.allowToContact &&
          props.listing.valueAddedServices?.isTenantPlusListing
        ),
      profileId: showProfileCheckbox.value && isProfileAttached.value ? profileId.value : undefined,
      accessToken,
      ...(shouldOptInToEmailVerification && {
        shouldOptInToEmailVerification: true,
      }),
      ...(isInEmailVerificationExperiment && {
        isInEmailVerificationExperiment: true,
      }),
      ...(isInEmailVerificationCodeExperiment && {
        isInEmailVerificationCodeExperiment: true,
      }),
    });

    persistContactFormContentSnapshot(contactInformation);
    recaptchaToken.value = '';
    emit('contactRequestDispatched');
  } finally {
    isRecaptchaSubmitInProgress.value = false;
  }
};

const openSellerLeadsPrivacyPopup = () => {
  emit('openPrivacyDeclarationModalVisible');
};
const saveContactedDate = (listingId: string) => {
  setContactedOnDateForListing(listingId);
};
const handleEmploymentStatusSelectedValue = (selectedValue: string) => {
  employmentStatus.value = selectedValue || undefined;
  if (employmentStatus.value) {
    hasExtraFinanceInformation.value = true;
  }
};
const handleMonthlyIncomeSelectedValue = (selectedValue: string) => {
  monthlyIncome.value = selectedValue || undefined;
  if (monthlyIncome.value) {
    hasExtraFinanceInformation.value = true;
  }
};
const handleEquityCapitalInputValue = (inputValue: number) => {
  equityCapital.value = inputValue ?? undefined;
  if (equityCapital.value) {
    hasExtraFinanceInformation.value = true;
  }
};
const onClickProfilePreviewCTA = () => {
  trackContactFormProfilePreviewClick();
  window.location.href = userProfileUrl.value;
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

$contact-form-shadow-color: rgba($color-grey-120, 0.2);
$text-area-line-number: 5;
$text-area-line-number-large: 8;
$text-area-line-height: 1.5;
$text-area-min-height: 130px;

.row {
  display: flex;
  gap: $space-100;

  > * {
    flex: 1;
  }

  > .addressCityInput {
    flex: 2;
  }
}

.wrapper {
  display: flex;
  flex-direction: column;
  gap: $space-100;
  overflow: hidden;
}

.contactFormWrapper {
  h3 {
    margin: 0;

    &.textCenter {
      text-align: center;
    }
  }

  p {
    margin: 0;
  }

  .textArea {
    textarea {
      min-height: $text-area-min-height;
    }
  }

  .submitButton {
    min-width: 100%;
  }

  .generalConditions,
  .sellerLeads {
    @include body-3;

    line-height: var(--typo-heading-2-line-height);

    .sellerLeadsPrivacyLink {
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
    }
  }

  .previewProfileText {
    padding: $space-50 0 $space-50 1.75rem;

    .previewProfileCTA {
      @include body-2-bold;
    }
  }

  &.containedInModal {
    padding: 0;
    box-shadow: none;

    textarea {
      min-height: $text-area-line-number-large * $text-area-line-height;
      resize: none;
    }
  }

  .agencyCardWrapper {
    margin: (-$space-100) (-$space-75) $space-100 (-$space-75);
  }
}

@media (max-width: $viewport-md-min) {
  .agencyCardWrapper {
    display: none;
  }
}

@media (min-width: $viewport-md-min) {
  .mobileContactAdvertiserHeader {
    display: none;
  }

  .contactFormWrapper {
    padding: $space-100;
    background: $color-white;
    border-radius: $border-radius;
    box-shadow: 0 0 $space-50 0 $contact-form-shadow-color;
  }
}

.gridWrapper {
  display: grid;
  grid-template-rows: 1fr;
  margin-top: 0;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 500ms;
  transition-property: grid-template-rows, margin-top;

  &.formCollapsed {
    grid-template-rows: 0fr;
    margin-top: -($space-100);
  }
}

@keyframes pulse-input {
  0% {
    background: $color-grey-30;
    box-shadow: 0 0 0 rgba($color-blue-70, 0.5);
    transform: scale(1);
  }

  50% {
    background: $color-blue-0;
    box-shadow: 0 0 $space-100 rgba($color-blue-70, 0.5);
    transform: scale(1.03);
  }

  100% {
    background: $color-grey-30;
    box-shadow: 0 0 0 rgba($color-blue-70, 0.5);
  }
}
</style>
